<template>
  <Popover align="right" width="600px" strategy="fixed">
    <template #trigger="{ toggleDropdown }">
      <LeadButton
        variant="ghost"
        size="sm"
        :icon="IconFilter"
        data-testid="orders-filter-button"
        @click.prevent="toggleDropdown">
        <span class="tw:hidden tw:md:inline">{{ $t('orders.list.filter_label') }}</span>
        <span
          v-if="activeFilterCount > 0"
          class="tw:inline-flex tw:h-4 tw:min-w-4 tw:items-center tw:justify-center tw:rounded-full tw:bg-primary tw:text-10 tw:text-white"
          data-testid="orders-filter-badge">
          {{ activeFilterCount }}
        </span>
      </LeadButton>
    </template>
    <template #dropdown="{ closeDropdown }">
      <div class="tw:grid tw:grid-cols-2 tw:gap-4 tw:p-3">
        <div>
          <LeadSelect
            v-model="draft.workplace_location"
            class="tw:mb-3"
            :label="$t('orders.filters.location')"
            :placeholder="$t('orders.filters.location')"
            :options="countryOptions"
            clearable />
          <LeadSelect
            v-model="draft.language"
            class="tw:mb-3"
            multiple
            clearable
            :label="$t('orders.filters.language')"
            :placeholder="$t('orders.filters.language')"
            :options="languageOptions" />
        </div>
        <div>
          <label class="form-label">{{ $t('orders.filters.time_range') }}</label>
          <div class="tw:my-3 tw:flex tw:flex-wrap tw:gap-2">
            <ToggleButton
              v-for="time in created_at_this"
              :active="draft.created_after === time.value"
              :text="time.label"
              :key="time.value"
              @click="draft.created_after = time.value" />
          </div>
          <hr />
          <div class="tw:flex tw:flex-wrap tw:gap-2">
            <ToggleButton
              v-for="time in created_at_last"
              :active="draft.created_after === time.value"
              :text="time.label"
              :key="time.value"
              @click="draft.created_after = time.value" />
          </div>
          <hr />
          <label class="form-label">{{ $t('order.status') }}</label>
          <div class="tw:flex tw:flex-wrap tw:gap-2">
            <ToggleButton
              v-for="s in order_statuses"
              :active="draft.lead_order_status === s.value"
              :text="s.label"
              :key="s.value"
              @click="draft.lead_order_status = s.value" />
          </div>
        </div>
      </div>
      <div class="tw:flex tw:items-center tw:justify-end tw:gap-3 tw:p-3">
        <LeadButton variant="ghost" @click="onReset(closeDropdown)">
          {{ $t('orders.filters.reset_filters') }}
        </LeadButton>
        <LeadButton variant="secondary" @click="onApply(closeDropdown)">
          {{ $t('orders.filters.apply') }}
        </LeadButton>
      </div>
    </template>
  </Popover>
</template>

<script setup lang="ts">
import { fetchAutocompleteFields } from '@/api/order';
import Popover from '@/components/common/Popover.vue';
import { IconFilter } from '@tabler/icons-vue';
import { computed, reactive, ref, onMounted, watch } from 'vue';
import { useToast } from 'vue-toastification';
import ToggleButton from '../common/ToggleButton.vue';
import LeadButton from '@/components/lead-design/LeadButton.vue';
import LeadSelect from '@/components/lead-design/LeadSelect.vue';
import translator from '@/locale/translator';

const created_at_this = [
  { label: translator.get('orders.filters.everything'), value: '' },
  { label: translator.get('orders.filters.today'), value: 'today' },
  { label: translator.get('orders.filters.thisWeek'), value: 'this_week' },
  { label: translator.get('orders.filters.thisMonth'), value: 'this_month' },
  { label: translator.get('orders.filters.thisYear'), value: 'this_year' },
];
const created_at_last = [
  { label: translator.get('orders.filters.last7'), value: '7days' },
  { label: translator.get('orders.filters.last30'), value: '30days' },
  { label: translator.get('orders.filters.last90'), value: '90days' },
];
const languageOptions = [
  { value: 'de', label: translator.get('language.de') },
  { value: 'en', label: translator.get('language.en') },
  { value: 'fr', label: translator.get('language.fr') },
  { value: 'it', label: translator.get('language.it') },
];
const order_statuses = [
  { label: translator.get('all'), value: '' },
  { label: translator.get('order.status.draft'), value: 'draft' },
  { label: translator.get('order.status.to_be_confirmed'), value: 'to_be_confirmed' },
  { label: translator.get('order.status.confirmed'), value: 'confirmed' },
  { label: translator.get('order.status.running'), value: 'running' },
  { label: translator.get('order.status.finished'), value: 'finished' },
];

// The parent owns the applied filters (source of truth: URL). This component
// keeps its own `draft` copy while the popover is open, and emits the draft on
// apply — the parent decides how to persist (URL push, memory update, etc.).
// `activeFilterCount` is computed by the parent too, so the badge here and the
// empty-state hint elsewhere always agree on what counts as "a filter".
const props = defineProps<{ modelValue: Record<string, any>; activeFilterCount?: number }>();

const emit = defineEmits<{
  (event: 'submitFilters', value: Record<string, any>): void;
  (event: 'resetFilters'): void;
}>();

// Draft mirrors the applied filters. Re-seeded whenever the applied set changes
// (e.g. URL-driven navigation, or after a successful apply) so the popover UI
// always reflects the currently-active filters when reopened.
const draft = reactive<Record<string, any>>({ ...props.modelValue });
watch(
  () => props.modelValue,
  (value) => Object.assign(draft, value),
  { deep: true }
);

function onApply(closeDropdown: () => void) {
  emit('submitFilters', { ...draft });
  closeDropdown();
}
function onReset(closeDropdown: () => void) {
  emit('resetFilters');
  closeDropdown();
}
const countries = ref<string[]>(['']);
const countryOptions = computed(() => countries.value.filter((c) => c !== '').map((c) => ({ value: c, label: c })));

const activeFilterCount = computed(() => props.activeFilterCount ?? 0);

onMounted(() => {
  fetchAutocompleteFields('workplace_location', { q: '' })
    .then((c) => (countries.value = c))
    .catch(() => useToast().error('Failed to catch available countries'));
});
</script>
